import React from 'react';
import { G } from '../theme.js';

// Lightweight per-widget boundary. Catches render errors in a small
// component and renders an optional `fallback` (or nothing) instead of
// letting the crash propagate to the screen-level boundary and blank
// the whole page. Reports to /api/client-errors.
export class Safe extends React.Component {
  state = { broken: false };
  static getDerivedStateFromError() { return { broken: true }; }
  componentDidCatch(err, info) {
    const name = this.props.name || 'unnamed';
    console.error(`[Safe:${name}]`, err, info?.componentStack);
    try {
      fetch('/api/client-errors', {
        method:'POST', credentials:'include',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({
          widget: name,
          message: String(err?.message || err),
          stack: String(err?.stack || ''),
          componentStack: info?.componentStack || '',
          url: location.href, userAgent: navigator.userAgent,
        }),
        keepalive: true,
      }).catch(()=>{});
    } catch {}
  }
  render() {
    if (this.state.broken) return this.props.fallback || null;
    return this.props.children;
  }
}

// Catches React render errors anywhere in the tree and shows a recovery UI
// instead of a blank white page. Also reports to /api/errors so the failure
// surfaces in System Health.
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null, info: null };
    // Track recent crashes so a stuck/repeat crash doesn't hide behind
    // auto-recovery. First transient crash → auto-heal in 400ms. Second
    // crash within 10s → still auto-heal but log. Third+ → show the
    // persistent overlay so the user knows something's really wrong.
    this.recentCrashes = [];
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(err, info) {
    this.setState({ info });
    // Log every crash to server for System Health
    try {
      fetch('/api/errors', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: err?.name || 'RenderError',
          message: err?.message || 'unknown render error',
          stack: (err?.stack || '') + '\n--- componentStack ---\n' + (info?.componentStack || ''),
          url: location.href,
          userAgent: navigator.userAgent,
          auto_recovered: this._willAutoRecover(),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
    // Prune old crashes + record this one
    const now = Date.now();
    this.recentCrashes = this.recentCrashes.filter(t => now - t < 10_000);
    this.recentCrashes.push(now);
    // Auto-recover on transient errors (< 3 crashes in 10 seconds)
    if (this._willAutoRecover()) {
      this._autoRecoverTimer = setTimeout(() => {
        this.setState({ err: null, info: null, copied: false });
      }, 400);
    }
  }
  _willAutoRecover() { return this.recentCrashes.length < 3; }
  componentWillUnmount() {
    if (this._autoRecoverTimer) clearTimeout(this._autoRecoverTimer);
  }
  reset = () => this.setState({ err: null, info: null, copied: false });
  reload = () => window.location.reload();
  copy = () => {
    const txt = `${this.state.err?.name || 'Error'}: ${this.state.err?.message || this.state.err}\n\n${this.state.err?.stack || ''}\n\nComponent stack:${this.state.info?.componentStack || ''}\n\nURL: ${location.href}`;
    try { navigator.clipboard.writeText(txt); this.setState({ copied: true }); setTimeout(()=>this.setState({copied:false}), 2000); } catch {}
  };
  render() {
    if (!this.state.err) return this.props.children;
    return <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, background: G.bg, color: G.text,
    }}>
      <div style={{
        maxWidth: 680, padding: 32, background: G.card,
        border: `1px solid ${G.cardBorder}`, borderRadius: 12,
      }}>
        <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Something went sideways</div>
        <div style={{ fontSize: 13, color: G.textMuted, marginBottom: 14, lineHeight: 1.5 }}>
          A page crashed. The error was logged — System Health will show it. The rest of the app is fine; reload the page or try a different tab.
        </div>
        <div style={{
          fontSize: 12, fontFamily: 'ui-monospace, monospace', color: G.red,
          padding: 10, background: G.input, borderRadius: 6, marginBottom: 10,
          wordBreak: 'break-word',
        }}>
          {String(this.state.err?.message || this.state.err)}
        </div>
        {this.state.err?.stack && <pre style={{
          fontSize: 11, fontFamily: 'ui-monospace, monospace', color: G.textMuted,
          padding: 10, background: G.input, borderRadius: 6, marginBottom: 14,
          maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap',
        }}>{String(this.state.err.stack)}{this.state.info?.componentStack ? `\n\nComponent stack:${this.state.info.componentStack}` : ''}</pre>}
        <div style={{ display: 'flex', gap: 10, flexWrap:'wrap' }}>
          <button onClick={this.copy} style={{
            padding: '10px 18px', background: this.state.copied?G.greenLight:G.input, color: this.state.copied?G.green:G.text,
            border: `1px solid ${G.cardBorder}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
          }}>{this.state.copied?'✓ Copied':'📋 Copy error'}</button>
          <button onClick={this.reload} style={{
            padding: '10px 18px', background: G.accent, color: '#fff', border: 'none',
            borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
          }}>Reload page</button>
          <button onClick={this.reset} style={{
            padding: '10px 18px', background: 'transparent', color: G.text,
            border: `1px solid ${G.cardBorder}`, borderRadius: 8, fontWeight: 600,
            cursor: 'pointer', fontSize: 14,
          }}>Try again</button>
        </div>
      </div>
    </div>;
  }
}
